import React, { useCallback, useMemo, useState } from 'react';
import { ApplicationAnalytics, orderedApplicationSteps } from '@app/api';
import {
  LabelList,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { StatsCard } from './StatsCard';

interface Props {
  analytics: ApplicationAnalytics[];
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    ...theme.fonts.label,
  },
  label: {
    ...theme.fonts.caption,
  },
}));

export const ApplicationFunnel = ({ analytics }: Props) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [overall, setOverall] = useState(false);
  const toggleOverall = () => setOverall((v) => !v);

  const data = useMemo(() => {
    const byPage = analytics.reduce((acc, track) => {
      if (track.fieldId) {
        return acc;
      }
      if (!acc[track.step]) {
        acc[track.step] = 0;
      }
      acc[track.step] += track.count;
      return acc;
    }, {} as Record<string, number>);

    const result = [];
    for (let i = 0; i < orderedApplicationSteps.length; i++) {
      const step = orderedApplicationSteps[i];
      const total = byPage[step] || 0;
      if (total === 0) {
        continue;
      }
      if (result.length === 0) {
        result.push({ step, total, percent: 1 });
        continue;
      }
      let prevTotal: number = result[result.length - 1]?.total || 0;
      if (overall) {
        prevTotal = result[0]?.total || 0;
      }
      result.push({ step, total, percent: total / prevTotal });
    }
    return result;
  }, [analytics, overall]);

  const toPercent = useCallback(
    (value: number) =>
      Intl.NumberFormat(language, { style: 'percent' }).format(value),
    [language],
  );

  return (
    <StatsCard
      title={t('sessions.details.stats.applications')}
      toggleOnChange={toggleOverall}
      toggleChecked={overall}
      toggleLabel={t('sessions.details.stats.conversions')}
    >
      <ResponsiveContainer height={300} className={classes.container}>
        <BarChart
          width={800}
          height={300}
          data={data}
          margin={{
            top: theme.marginBase * 3,
            right: theme.marginBase * 3,
            bottom: theme.marginBase * 3,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="step" tick={theme.fonts.caption} />
          <YAxis tick={theme.fonts.caption} />
          <Tooltip />
          <Bar dataKey="total" fill={theme.colors.accentAccessible}>
            <LabelList
              className={classes.label}
              dataKey="percent"
              position="top"
              fill={theme.colors.inkSecondary}
              formatter={toPercent}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </StatsCard>
  );
};
